import { useMemo, useState } from 'react';
import { Card, Typography, Tag, Button, Modal, Form, Input, InputNumber, Select, Tooltip, Space, Badge, Switch, Avatar, EmptyState, Tabs, theme, message, Drawer, Row, Col } from '../../ui';
import { Plus, Pencil, MapPin, Filter, Eraser, SlidersHorizontal, User, UserPlus, Phone, Mail } from 'lucide-react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WarehouseActivityPage } from '../inventory/WarehouseActivityPage';
import { WarehouseManagersPage } from '../admin/WarehouseManagersPage';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { usePermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { LocationPicker } from '../../components/LocationPicker';
import { STATE_OPTIONS } from '../../constants/indianStates';
import { WarehouseReorderModal } from './WarehouseReorderModal';

export function WarehousesPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [reorderFor, setReorderFor] = useState<{ id: string; code: string; name: string } | null>(null);
  const [managerFor, setManagerFor] = useState<any>(null); // warehouse whose manager(s) are shown in a drawer
  const [assignFor, setAssignFor] = useState<any>(null);   // warehouse for which the assign-manager modal is open
  const [expandedKeys, setExpandedKeys] = useState<any[]>([]); // which warehouse rows are expanded (controls the red highlight)
  // Creating / editing a warehouse needs WAREHOUSE.ADMIN (the backend enforces
  // it); plain WAREHOUSE.VIEW users get a read-only list.
  const canManage = usePermission('WAREHOUSE.ADMIN');
  // Setting per-warehouse minimum stock levels is an Admin/SA action (PRODUCT.EDIT).
  const canSetLevels = usePermission('PRODUCT.EDIT');
  const canAssignMgr = usePermission('WAREHOUSE.MANAGER.ASSIGN');
  // Active tab from ?tab= — 'managers' only when the user can assign managers (else that tab isn't rendered).
  const rawTab = sp.get('tab');
  const tab = rawTab === 'activity' ? 'activity' : (rawTab === 'managers' && canAssignMgr) ? 'managers' : 'list';

  // ---- Filters (resolved server-side) ----
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [cityFilter, setCityFilter] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<'true' | 'false' | 'all'>('true');

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (search.trim()) p.q = search.trim();
    if (stateFilter.length) p.state = stateFilter.join(',');
    if (cityFilter.length) p.city = cityFilter.join(',');
    if (activeFilter !== 'true') p.active = activeFilter; // backend defaults to active-only
    return p;
  }, [search, stateFilter, cityFilter, activeFilter]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['warehouses-full', params],
    queryFn: () => request<any[]>({ url: '/warehouses', params }),
    placeholderData: keepPreviousData,
  });

  // Facets + counts over ALL warehouses (incl. inactive), independent of current filters.
  const { data: facets } = useQuery({
    queryKey: ['warehouse-facets'],
    queryFn: () => request<any>({ url: '/warehouses/filter-options' }),
  });

  const rows = data ?? [];
  const stateOptions = facets?.states ?? [];
  const cityOptions = facets?.cities ?? [];
  const total = facets?.total ?? 0;

  // KPI roll-ups over the shown warehouses.
  const lowWarehouses = rows.filter((r: any) => Number(r.lowStockCount) > 0).length;
  const lowItems = rows.reduce((s: number, r: any) => s + Number(r.lowStockCount || 0), 0);
  const unassigned = rows.filter((r: any) => !(r.managers?.length)).length;

  const activeFilters =
    (search ? 1 : 0) + (stateFilter.length ? 1 : 0) +
    (cityFilter.length ? 1 : 0) + (activeFilter !== 'true' ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStateFilter([]); setCityFilter([]); setActiveFilter('true');
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'code', title: 'Code', dataIndex: 'code', sortable: true, alwaysVisible: true },
    {
      key: 'name', title: 'Name', dataIndex: 'name', sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <span>{v}{Number(r.lowStockCount) > 0 && <Pill color="red" className="ml-2">{r.lowStockCount} low</Pill>}</span>
      ),
    },
    {
      key: 'isMaster', title: 'Master', dataIndex: 'isMaster', sortable: true, width: 90,
      render: (v: boolean) => (v ? <Pill color="gold">Master</Pill> : null),
    },
    { key: 'city', title: 'City', dataIndex: 'city', sortable: true },
    { key: 'stateCode', title: 'State', dataIndex: 'stateCode', sortable: true, width: 80 },
    { key: 'pincode', title: 'Pincode', dataIndex: 'pincode', defaultVisible: false },
    { key: 'gstNo', title: 'GST', dataIndex: 'gstNo', defaultVisible: false },
    {
      key: 'geofence', title: 'Geofence (m)', dataIndex: 'geofenceRadiusM',
      sortable: true, width: 130, defaultVisible: false,
    },
    {
      key: 'manager', title: 'Manager', alwaysVisible: true, width: 190,
      render: (_: any, r: any) => {
        const mgrs = r.managers ?? [];
        if (!mgrs.length) {
          // Only roles with WAREHOUSE.MANAGER.ASSIGN can bind a manager; everyone else just sees the state.
          return canAssignMgr
            ? <Button variant="text" size="sm" icon={<UserPlus size={16} />} style={{ padding: 0 }} onClick={(e) => { e.stopPropagation(); setAssignFor(r); }}>Assign</Button>
            : <span style={{ color: token.colorTextTertiary }}>Unassigned</span>;
        }
        return (
          <a onClick={(e) => { e.stopPropagation(); setManagerFor(r); }}>
            {mgrs[0].name}{mgrs.length > 1 ? ` +${mgrs.length - 1}` : ''}
          </a>
        );
      },
    },
    {
      key: 'isActive', title: 'Active', dataIndex: 'isActive', sortable: true, width: 90,
      render: (v: boolean) => v ? <Pill color="green">Yes</Pill> : <Pill>No</Pill>,
    },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 150,
      render: (_: any, r: any) => (
        <Space size={4}>
          {canManage && (
            <Tooltip label="Edit">
              <Button variant="text" size="sm" aria-label="Edit" icon={<Pencil size={16} />}
                onClick={(e) => { e.stopPropagation(); setEditing(r); setOpen(true); }} />
            </Tooltip>
          )}
          {canSetLevels && (
            <Tooltip label="Minimum stock levels">
              <Button variant="text" size="sm" aria-label="Minimum stock levels" icon={<SlidersHorizontal size={16} />}
                onClick={(e) => { e.stopPropagation(); setReorderFor({ id: String(r.id), code: r.code, name: r.name }); }} />
            </Tooltip>
          )}
          <Tooltip label="Map">
            <Button variant="text" size="sm" aria-label="Map" icon={<MapPin size={16} />}
              onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps/search/?api=1&query=${r.latitude},${r.longitude}`, '_blank', 'noopener'); }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>Warehouses</Typography.Title>
      <Tabs
        activeKey={tab}
        onChange={(k) => setSp({ tab: k }, { replace: true })}
        items={[{
          key: 'list',
          label: 'Warehouses',
          children: (
            <>
      {/* Header actions — moved out of the table toolbar, above the filters. */}
      {canManage && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          <Button variant="primary" size="sm" icon={<Plus size={16} />}
            onClick={() => { setEditing(null); setOpen(true); }}>
            Add warehouse
          </Button>
        </div>
      )}
      <div className="mb-3.5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-card border border-line bg-surface p-4 shadow-soft">
          <div className="text-xs text-muted">Warehouses</div>
          <div className="text-2xl font-semibold text-content">{total || rows.length}</div>
        </div>
        <div className="rounded-card border border-line bg-surface p-4 shadow-soft">
          <div className="text-xs text-muted">Low-stock warehouses</div>
          <div className={`text-2xl font-semibold ${lowWarehouses ? 'text-danger-600' : 'text-content'}`}>{lowWarehouses}</div>
        </div>
        <div className="rounded-card border border-line bg-surface p-4 shadow-soft">
          <div className="text-xs text-muted">Low-stock items</div>
          <div className={`text-2xl font-semibold ${lowItems ? 'text-danger-600' : 'text-content'}`}>{lowItems}</div>
        </div>
        <div className="rounded-card border border-line bg-surface p-4 shadow-soft">
          <div className="text-xs text-muted">Unassigned</div>
          <div className={`text-2xl font-semibold ${unassigned ? 'text-warning-600' : 'text-content'}`}>{unassigned} <span className="text-xs font-normal text-subtle">no manager</span></div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">{/* filter controls (Antd) */}
          <Input
            placeholder="Search code, name, city, GST"
            prefix={<Filter size={16} style={{ color: token.colorTextTertiary }} />}
            style={{ width: 250 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Select
            mode="multiple" allowClear placeholder="State" style={{ minWidth: 130 }}
            maxTagCount={1} value={stateFilter} onChange={setStateFilter} options={stateOptions}
          />
          <Select
            mode="multiple" allowClear placeholder="City" style={{ minWidth: 160 }}
            maxTagCount={1} value={cityFilter} onChange={setCityFilter} options={cityOptions}
          />
          <Select
            style={{ width: 160 }}
            value={activeFilter}
            onChange={(v) => setActiveFilter(v)}
            options={[
              { value: 'true', label: 'Active only' },
              { value: 'false', label: 'Inactive only' },
              { value: 'all', label: 'All statuses' },
            ]}
          />
          {activeFilters > 0 && (
            <Badge count={activeFilters} size="small">
              <Button icon={<Eraser size={16} />} onClick={clearFilters}>Clear</Button>
            </Badge>
          )}
      </div>

      <TwDataTable
        storageKey="warehouses"
        rowKey="id"
        loading={isLoading || isFetching}
        dataSource={rows}
        columns={columns}
        rowClassName={(r: any) => (Number(r.lowStockCount) > 0 && !expandedKeys.map(String).includes(String(r.id)) ? 'gb-row-low' : '')}
        expandable={{
          expandedRowRender: (r: any) => <WarehouseStock id={String(r.id)} />,
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => setExpandedKeys(keys as any[]),
        }}
        onRow={(r) => ({
          // Clicking a row toggles its stock breakdown; editing is via the pencil action button.
          onClick: () => setExpandedKeys((keys) =>
            keys.map(String).includes(String(r.id)) ? keys.filter((k) => String(k) !== String(r.id)) : [...keys, r.id]),
        })}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText="No warehouses"
        toolbarLeft={
          <span className="text-sm text-muted">
            {rows.length === total ? `${total} location${total === 1 ? '' : 's'}` : `${rows.length} of ${total} locations`}
            {activeFilters > 0 ? ` · ${activeFilters} filter${activeFilters === 1 ? '' : 's'}` : ''}
          </span>
        }
      />
            </>
          ),
        }, {
          key: 'activity',
          label: 'Activity',
          children: <WarehouseActivityPage embedded />,
        },
        // Manager assignment (was the standalone Admin → Warehouse Managers page) — shown to WAREHOUSE.MANAGER.ASSIGN.
        ...(canAssignMgr ? [{
          key: 'managers',
          label: 'Managers',
          children: <WarehouseManagersPage embedded />,
        }] : [])]}
      />

      <WarehouseFormModal open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} />

      <WarehouseReorderModal warehouse={reorderFor} onClose={() => setReorderFor(null)} />

      <Drawer
        open={!!managerFor}
        onClose={() => setManagerFor(null)}
        width={420}
        title={managerFor ? `Warehouse manager — ${managerFor.code}` : 'Warehouse manager'}
      >
        {(managerFor?.managers ?? []).length === 0 ? (
          <EmptyState title="No manager assigned to this warehouse." />
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {(managerFor?.managers ?? []).map((m: any) => (
              <Card key={m.id}>
                <Space align="start">
                  <Avatar size={44} src={m.photoUrl || undefined} fallback={<User size={16} />} />
                  <div>
                    <div><b>{m.name}</b> {m.role && <Tag style={{ marginLeft: 4 }}>{m.role}</Tag>}</div>
                    {m.phone && <div style={{ marginTop: 4 }}><Phone size={16} /> <a href={`tel:${m.phone}`}>{m.phone}</a></div>}
                    {m.email && <div><Mail size={16} /> <a href={`mailto:${m.email}`}>{m.email}</a></div>}
                  </div>
                </Space>
              </Card>
            ))}
          </Space>
        )}
        {canAssignMgr && (
          <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
            <Button variant="primary" block icon={<UserPlus size={16} />} onClick={() => { const w = managerFor; setManagerFor(null); setAssignFor(w); }}>
              Assign {(managerFor?.managers ?? []).length ? 'another manager' : 'manager'}
            </Button>
            <Button block onClick={() => { setManagerFor(null); navigate('/admin/warehouse-managers'); }}>
              Manage assignments
            </Button>
          </Space>
        )}
      </Drawer>

      <AssignManagerModal warehouse={assignFor} onClose={() => setAssignFor(null)} />
    </div>
  );
}

// Quick "assign a Warehouse Manager to this warehouse" modal, opened from the Manager column / drawer.
// Gated upstream by WAREHOUSE.MANAGER.ASSIGN; the backend re-checks the same permission.
function AssignManagerModal({ warehouse, onClose }: { warehouse: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const open = !!warehouse;
  const [userId, setUserId] = useState<string>();
  const [isPrimary, setIsPrimary] = useState(true);
  const [note, setNote] = useState('');

  // Users that hold the Warehouse Manager role — the only ones eligible to be assigned.
  const { data: wmUsers, isLoading } = useQuery({
    queryKey: ['wm-users'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'WAREHOUSE_MANAGER', pageSize: 200 } }),
    enabled: open,
  });
  const assignedIds = new Set((warehouse?.managers ?? []).map((m: any) => String(m.id)));
  const options = (wmUsers?.data ?? [])
    .filter((u: any) => !assignedIds.has(String(u.id)))
    .map((u: any) => ({ value: String(u.id), label: `${u.fullName} · ${u.mobile}` }));

  const close = () => { setUserId(undefined); setIsPrimary(true); setNote(''); onClose(); };

  const assign = useMutation({
    mutationFn: () => request({ url: '/warehouse-assignments', method: 'POST', data: { userId, warehouseId: String(warehouse.id), isPrimary, note: note.trim() || undefined } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouses-full'] });
      qc.invalidateQueries({ queryKey: ['warehouse-facets'] });
      qc.invalidateQueries({ queryKey: ['warehouse-assignments'] });
      message.success('Warehouse manager assigned');
      close();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to assign manager'),
  });

  return (
    <Modal
      open={open} onOpenChange={(o) => { if (!o) close(); }}
      title={warehouse ? `Assign manager · ${warehouse.code} — ${warehouse.name}` : 'Assign manager'}
      footer={<><Button variant="default" onClick={close}>Cancel</Button><Button variant="primary" loading={assign.isPending} disabled={!userId} onClick={() => assign.mutate()}>Assign</Button></>}
    >
      <Form layout="vertical">
        <Form.Item label="Warehouse Manager" required>
          <Select
            showSearch optionFilterProp="label" placeholder="Select a Warehouse Manager"
            style={{ width: '100%' }} loading={isLoading} value={userId} onChange={setUserId} options={options}
            notFoundContent={isLoading ? 'Loading…' : 'No users with the Warehouse Manager role'}
          />
        </Form.Item>
        <Form.Item label="Primary manager" tooltip="The main point of contact for this warehouse.">
          <Switch checked={isPrimary} onCheckedChange={setIsPrimary} />
        </Form.Item>
        <Form.Item label="Note (optional)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. interim cover" />
        </Form.Item>
      </Form>
      <Typography.Paragraph type="secondary" className="text-xs" style={{ marginBottom: 0 }}>
        Only users with the <b>Warehouse Manager</b> role appear here.{' '}
        <a onClick={() => { close(); navigate('/admin/warehouse-managers'); }}>Open manager admin</a> for full control.
      </Typography.Paragraph>
    </Modal>
  );
}

// Product-wise on-hand stock for a warehouse — lazy-loaded when its row is expanded.
function WarehouseStock({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['warehouse-stock', id],
    queryFn: () => request<any[]>({ url: `/warehouses/${id}/stock` }),
  });
  const rows = data ?? [];
  const TH = 'px-3 py-2.5 text-2xs font-semibold uppercase tracking-wide text-muted';
  return (
    <div className="px-3 py-2">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className={`${TH} text-left`}>Product</th>
            <th className={`${TH} text-right`}>On hand</th>
            <th className={`${TH} text-right`}>Minimum</th>
            <th className={`${TH} text-right`}>Batches</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={4} className="px-3 py-3"><div className="h-5 animate-pulse rounded bg-surface-3" /></td></tr>
          ) : rows.length ? (
            rows.map((r: any) => {
              const low = Number(r.minLevel) > 0 && Number(r.available) <= Number(r.minLevel);
              return (
                <tr key={r.productId} className={`border-t border-line ${low ? 'gb-row-low' : ''}`}>
                  <td className="px-3 py-2.5 text-content">{r.sku} · {r.productName}</td>
                  <td className={`px-3 py-2.5 text-right font-semibold ${low ? 'text-danger-600' : 'text-content'}`}>{fmtQty(r.available)}</td>
                  <td className="px-3 py-2.5 text-right text-muted">{Number(r.minLevel) > 0 ? fmtQty(r.minLevel) : <span className="text-subtle">—</span>}</td>
                  <td className="px-3 py-2.5 text-right text-muted">{r.batches}</td>
                </tr>
              );
            })
          ) : (
            <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-subtle">No stock in this warehouse</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function WarehouseFormModal({ open, onClose, record }: { open: boolean; onClose: () => void; record?: any }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const isEdit = !!record;

  // Seed via initialValues (record is available synchronously at open time) + a per-target key on the
  // <Form> below, so the remounting Form store is seeded correctly at render each open — no
  // setFieldsValue-vs-remount race (blank fields) and no stale code/id leaking from a prior open.
  const initialValues = record
    ? { ...record, latitude: Number(record.latitude), longitude: Number(record.longitude) }
    : { geofenceRadiusM: 200, isActive: true, isMaster: false };

  const save = useMutation({
    mutationFn: (vals: any) => {
      const payload = { ...vals, latitude: String(vals.latitude), longitude: String(vals.longitude) };
      return isEdit
        ? request({ url: `/warehouses/${record.id}`, method: 'PUT', data: payload })
        : request({ url: '/warehouses', method: 'POST', data: payload });
    },
    onSuccess: () => {
      message.success(isEdit ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['warehouses-full'] });
      qc.invalidateQueries({ queryKey: ['warehouse-facets'] });
      qc.invalidateQueries({ queryKey: ['warehouses'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={isEdit ? `Edit · ${record?.code}` : 'New warehouse'}
      width={780}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Update' : 'Create'}</Button></>}>
      <Form form={form} key={record ? `edit-${record.id}` : 'create'} initialValues={initialValues} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Row gutter={[12, 12]}>
          {/* Row 1 — basics (two columns) */}
          <Col span={12}>
            <Form.Item name="code" label="Code" rules={[{ required: true }]}>
              <Input placeholder="WH-XYZ" disabled={isEdit} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="isMaster" label="Master warehouse" valuePropName="checked"
              tooltip="The single stock-origin point — stock is added directly here; all other warehouses receive it via transfer. Turning this on moves the master to this warehouse.">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="geofenceRadiusM" label="Geofence radius (m)">
              <InputNumber style={{ width: '100%' }} min={50} step={50} />
            </Form.Item>
          </Col>

          {/* Map picker (full width) */}
          <Col span={24}>
            <LocationPicker fields={{ address: 'addressLine1', state: 'stateCode' }} required />
          </Col>

          {/* Address & details — two columns */}
          <Col span={12}>
            <Form.Item name="addressLine1" label="Address line 1" rules={[{ required: true }]}><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="addressLine2" label="Address line 2"><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="city" label="City" rules={[{ required: true }]}><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="stateCode" label="State" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select state"
                options={STATE_OPTIONS}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="pincode" label="Pincode" rules={[{ required: true }]}>
              <Input maxLength={6} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="gstNo" label="GST number">
              <Input placeholder="27AABCG0000Z1Z5" maxLength={15} />
            </Form.Item>
          </Col>

          {/* Coordinates come from the map picker above — kept as hidden required fields. */}
          <Form.Item name="latitude" hidden rules={[{ required: true, message: 'Pick a location on the map' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden rules={[{ required: true, message: 'Pick a location on the map' }]}>
            <Input />
          </Form.Item>
        </Row>
      </Form>
    </Modal>
  );
}
